/* eslint-disable */
import type { Money } from "../../common/currency.pb";
import type { PayrollProviderUiSpecification } from "../../payroll-connector/common/PayrollProviderProto.pb";
import type { LocalizedString } from "../localization/localization.pb";
import type { Image } from "../ui/Image.pb";

/** An allocation destination of the Cash Balance. */
export interface CashBalanceDestination {
}

/** An allocation destination into a Savings Folder */
export interface SavingsDestination {
}

/** An allocation destination into Bitcoin */
export interface BitcoinDestination {
}

/** An allocation destination into a specific Investment */
export interface InvestingDestination {
  /**
   * Uniquely identifies a
   * squareup.cash.marketdata.model.InvestmentEntity
   */
  investment_entity_token?: string;
  /**
   * The Ui specification for displaying this investing choice.
   * The contents of this property are provided for Ui config
   * purposes and do not contribute to the destination's identity, which
   * is provided by `investment_entity_token`
   */
  ui_specification?: InvestingDestinationUiSpecification;
}

export interface InvestingDestinationUiSpecification {
  /** The name to attach to an investing choice. */
  display_name?: string;
  /** The symbol to attach to an investing choice */
  symbol?: string;
  /** The image to attach to an investing choice */
  icon?: Image;
}

/**
 * For most of these enumerated destination types, there is not
 * currently any additional state require to uniquely identify a share
 * to be distributed. This may change in the future. A hypothetical
 * example would be to allow a user multiple Savings balances that
 * correspond to different financial goals.
 *
 * This structure currently supports the purchase of more than one
 * Investing destination based on its investment. The initial version
 * of this feature will not allow for that. Server and client should
 * both validate this when handling this type.
 */
export interface AllocationDestination {
  cashBalance?: CashBalanceDestination | undefined;
  savings?: SavingsDestination | undefined;
  bitcoin?: BitcoinDestination | undefined;
  investing?: InvestingDestination | undefined;
}

/**
 * An amount subtracted from a Paycheck before it is allocated towards
 * the destinations specified in the allocation distribution
 */
export interface Deduction {
  /** The amount deducted */
  amount?: Money;
  /** A description of the deduction. User facing. eg 'Instant Paycheck' */
  description?: LocalizedString;
}

/**
 * The specific destination's amount produced by applying an
 * AllocationDistribution to a paycheck after subtracting deductions and
 * accounting for amount restrictions (minimums, atomic transaction
 * sizes, etc).
 */
export interface RealizedAllocationAmount {
  /** The destination allocation type */
  destination?: AllocationDestination;
  /**
   * The realized amount after applying the basis points for that destination
   * and correcting for restrictions.
   */
  realized_amount?: Money;
  /** The current state of this realized amount */
  state?: RealizedAllocationAmountState;
  /**
   * A note that can be shown in the Ui. Use for things like informing
   * the user that their investment cannot be purchased until market
   * hours.
   */
  note?: LocalizedString;
}

export enum RealizedAllocationAmountState {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  ERRORED = "ERRORED",
}

/**
 * A definition of employers that provides both a display specifier
 * (via `ui_specification`) and an equality definition (via
 * `employer_token`.)
 *
 * Some employers we know very little about may be "empty",
 * in the sense that they have no employer_token or UiSpec.
 * We will use the generic employer avatar for them when displaying
 * the receipt view.
 */
export interface Employer {
  /**
   * A foreign key to `payrollconnector.common.Employer`. Used to
   * uniquely identify employers for aggregation or other purposes.
   */
  employer_token?: string;
  /** Components needed to display a representation of this employer */
  ui_specification?: PayrollProviderUiSpecification;
}

/** A Ui specification for an alert */
export interface AlertUi {
  title_text?: LocalizedString;
  detail_text?: LocalizedString;
  acknowledge_text?: LocalizedString;
  cancel_text?: LocalizedString;
}

/**
 * Template string for clients to display the estimated amount
 * per paycheck. Format specifier is %s for Android and %@ for iOS.
 */
export interface LocalizedTemplatedString {
  /** Example: "%s based on average monthly paycheck" */
  android?: LocalizedString;
  /** Example: "%@ based on average monthly paycheck" */
  ios?: LocalizedString;
}
